import { Link } from 'react-router-dom';
import {
  User,
  MapPin,
  Phone,
  Mail,
  Building,
  Calendar,
  CheckCircle,
  XCircle,
  Ban,
  Shield,
  TrendingUp,
  Wallet,
  Users,
  ArrowLeft,
} from 'lucide-react';
import { Badge } from '@medikei/ui';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@medikei/ui';
import { LoadingOverlay } from '@/components/ui/loading-overlay';
import type {
  Partner,
  PartnerStatus,
  KYCStatus,
  PartnerLevel,
  Commission,
  Payout,
} from '../types/admin-partner';

interface PartnerDetailProps {
  partner: Partner;
  commissions: Commission[];
  payouts: Payout[];
  isLoading: boolean;
  onApprove: () => void;
  onReject: () => void;
  onSuspend: () => void;
  onApproveKYC: () => void;
  onChangeLevel: (level: PartnerLevel) => void;
  isApproving?: boolean;
  isRejecting?: boolean;
  isSuspending?: boolean;
  isApprovingKYC?: boolean;
  isChangingLevel?: boolean;
}

const statusVariants: Record<PartnerStatus, 'warning' | 'success' | 'danger' | 'outline'> = {
  PENDING: 'warning',
  APPROVED: 'success',
  ACTIVE: 'success',
  REJECTED: 'danger',
  SUSPENDED: 'outline',
};

const statusLabels: Record<PartnerStatus, string> = {
  PENDING: 'Pendente',
  APPROVED: 'Aprovado',
  ACTIVE: 'Ativo',
  REJECTED: 'Rejeitado',
  SUSPENDED: 'Suspenso',
};

const kycVariants: Record<KYCStatus, 'warning' | 'success' | 'danger' | 'outline'> = {
  PENDING: 'warning',
  SUBMITTED: 'warning',
  VERIFIED: 'success',
  APPROVED: 'success',
  REJECTED: 'danger',
  NOT_SUBMITTED: 'outline',
};

const kycLabels: Record<KYCStatus, string> = {
  PENDING: 'Pendente',
  SUBMITTED: 'Enviado',
  VERIFIED: 'Verificado',
  APPROVED: 'Aprovado',
  REJECTED: 'Rejeitado',
  NOT_SUBMITTED: 'Não Enviado',
};

const levelLabels: Record<PartnerLevel, string> = {
  BRONZE: 'Bronze',
  SILVER: 'Prata',
  GOLD: 'Ouro',
  PLATINUM: 'Platina',
};

export function PartnerDetail({
  partner,
  commissions,
  payouts,
  isLoading,
  onApprove,
  onReject,
  onSuspend,
  onApproveKYC,
  onChangeLevel,
  isApproving,
  isRejecting,
  isSuspending,
  isApprovingKYC,
  isChangingLevel,
}: PartnerDetailProps) {
  const formatDate = (dateStr: string) => {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(new Date(dateStr));
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(amount);
  };

  const formatCpfCnpj = (value: string) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length === 11) {
      return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }
    if (digits.length === 14) {
      return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
    return value;
  };

  if (isLoading) {
    return <LoadingOverlay message="Carregando detalhes..." />;
  }

  const totalCommissions = commissions.reduce((sum, c) => sum + c.amount, 0);
  const totalPayouts = payouts.reduce((sum, p) => sum + p.amount, 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Link to="/admin/partners">
          <Button variant="ghost" size="sm">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar
          </Button>
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Informações do Parceiro
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Nome</p>
                  <p className="font-medium">{partner.name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">E-mail</p>
                  <p className="font-medium flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    {partner.email}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">CPF/CNPJ</p>
                  <p className="font-medium">{formatCpfCnpj(partner.cpfCnpj)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Telefone</p>
                  <p className="font-medium flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    {partner.phone || '-'}
                  </p>
                </div>
                <div className="col-span-2">
                  <p className="text-sm text-muted-foreground">Endereço</p>
                  <p className="font-medium flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    {partner.address
                      ? `${partner.address}, ${partner.city} - ${partner.state}`
                      : '-'}
                  </p>
                </div>
              </div>

              <div className="flex gap-2 pt-4 border-t">
                {partner.status === 'PENDING' && (
                  <>
                    <Button
                      variant="primary"
                      onClick={onApprove}
                      disabled={isApproving}
                      isLoading={isApproving}
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Aprovar
                    </Button>
                    <Button
                      variant="outline"
                      onClick={onReject}
                      disabled={isRejecting}
                      isLoading={isRejecting}
                      className="text-destructive hover:text-destructive"
                    >
                      <XCircle className="h-4 w-4 mr-2" />
                      Rejeitar
                    </Button>
                  </>
                )}
                {(partner.status === 'APPROVED' || partner.status === 'ACTIVE') && (
                  <Button
                    variant="outline"
                    onClick={onSuspend}
                    disabled={isSuspending}
                    isLoading={isSuspending}
                    className="text-warning hover:text-warning"
                  >
                    <Ban className="h-4 w-4 mr-2" />
                    Suspender
                  </Button>
                )}
                {partner.status === 'SUSPENDED' && (
                  <Button
                    variant="primary"
                    onClick={onApprove}
                    disabled={isApproving}
                    isLoading={isApproving}
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Ativar Parceiro
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Verificação KYC
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  <Badge variant={kycVariants[partner.kycStatus]}>
                    {kycLabels[partner.kycStatus]}
                  </Badge>
                </div>
                {(partner.kycStatus === 'PENDING' || partner.kycStatus === 'SUBMITTED') && (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={onApproveKYC}
                    disabled={isApprovingKYC}
                    isLoading={isApprovingKYC}
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Aprovar KYC
                  </Button>
                )}
              </div>

              {partner.kycData && (
                <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                  <div>
                    <p className="text-sm text-muted-foreground">Tipo de Documento</p>
                    <p className="font-medium">{partner.kycData.documentType}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Número do Documento</p>
                    <p className="font-medium">{partner.kycData.documentNumber}</p>
                  </div>
                  {partner.kycData.submittedAt && (
                    <div>
                      <p className="text-sm text-muted-foreground">Enviado em</p>
                      <p className="font-medium">
                        {formatDate(partner.kycData.submittedAt)}
                      </p>
                    </div>
                  )}
                  {partner.kycData.verifiedAt && (
                    <div>
                      <p className="text-sm text-muted-foreground">Verificado em</p>
                      <p className="font-medium">
                        {formatDate(partner.kycData.verifiedAt)}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {partner.kycStatus === 'REJECTED' && partner.kycData?.rejectionReason && (
                <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg mt-4">
                  <p className="text-sm font-medium text-destructive">Motivo da Rejeição:</p>
                  <p className="text-sm">{partner.kycData.rejectionReason}</p>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Histórico de Comissões</CardTitle>
            </CardHeader>
            <CardContent>
              {commissions.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  Nenhuma comissão encontrada.
                </p>
              ) : (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="px-4 py-2 text-left text-sm">Paciente</th>
                        <th className="px-4 py-2 text-left text-sm">Valor</th>
                        <th className="px-4 py-2 text-left text-sm">Status</th>
                        <th className="px-4 py-2 text-left text-sm">Data</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {commissions.slice(0, 5).map((commission) => (
                        <tr key={commission.id}>
                          <td className="px-4 py-2 text-sm">{commission.patientName}</td>
                          <td className="px-4 py-2 text-sm font-medium">
                            {formatCurrency(commission.amount)}
                          </td>
                          <td className="px-4 py-2">
                            <Badge
                              variant={
                                commission.status === 'PAID'
                                  ? 'outline'
                                  : commission.status === 'AVAILABLE'
                                  ? 'success'
                                  : commission.status === 'PENDING'
                                  ? 'warning'
                                  : 'danger'
                              }
                            >
                              {commission.status}
                            </Badge>
                          </td>
                          <td className="px-4 py-2 text-sm text-muted-foreground">
                            {formatDate(commission.createdAt)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Status e Nível</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm text-muted-foreground">Status</p>
                <Badge variant={statusVariants[partner.status]} className="mt-1">
                  {statusLabels[partner.status]}
                </Badge>
              </div>

              <div>
                <p className="text-sm text-muted-foreground">Nível Atual</p>
                <p className="text-lg font-bold mt-1">{levelLabels[partner.level]}</p>
              </div>

              <div className="flex flex-wrap gap-2">
                {(['BRONZE', 'SILVER', 'GOLD', 'PLATINUM'] as PartnerLevel[]).map((level) => (
                  <Button
                    key={level}
                    variant={partner.level === level ? 'primary' : 'outline'}
                    size="sm"
                    onClick={() => onChangeLevel(level)}
                    disabled={partner.level === level || isChangingLevel}
                  >
                    {levelLabels[level]}
                  </Button>
                ))}
              </div>

              <div className="pt-4 border-t">
                <p className="text-sm text-muted-foreground">Desde</p>
                <p className="font-medium flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  {formatDate(partner.createdAt)}
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Estatísticas</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-muted/50 rounded-lg text-center">
                  <TrendingUp className="h-5 w-5 mx-auto text-primary mb-1" />
                  <p className="text-2xl font-bold">{partner.stats?.totalReferrals || 0}</p>
                  <p className="text-xs text-muted-foreground">Indicações</p>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg text-center">
                  <Users className="h-5 w-5 mx-auto text-primary mb-1" />
                  <p className="text-2xl font-bold">
                    {partner.stats?.conversionRate?.toFixed(1) || 0}%
                  </p>
                  <p className="text-xs text-muted-foreground">Conversão</p>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg text-center">
                  <Wallet className="h-5 w-5 mx-auto text-primary mb-1" />
                  <p className="text-2xl font-bold">{formatCurrency(totalCommissions)}</p>
                  <p className="text-xs text-muted-foreground">Comissões</p>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg text-center">
                  <Wallet className="h-5 w-5 mx-auto text-primary mb-1" />
                  <p className="text-2xl font-bold">{formatCurrency(totalPayouts)}</p>
                  <p className="text-xs text-muted-foreground">Pagamentos</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {partner.bankAccount && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building className="h-5 w-5" />
                  Dados Bancários
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Banco</span>
                  <span className="text-sm font-medium">
                    {partner.bankAccount.bankName} ({partner.bankAccount.bankCode})
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Agência</span>
                  <span className="text-sm font-medium">{partner.bankAccount.agency}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Conta</span>
                  <span className="text-sm font-medium">{partner.bankAccount.account}</span>
                </div>
                {partner.bankAccount.pixKey && (
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">PIX</span>
                    <span className="text-sm font-medium">{partner.bankAccount.pixKey}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
